import { useRef, useState } from 'react';

import { Box, ButtonWithDropdown, Modal, Text } from '@simplywallst/ui-core';
import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import EditOutline from '@simplywallst/ui-core/icons/EditOutline';
import BinSolid from '@simplywallst/ui-core/icons/BinSolid';
import If from '@components/If';

import { useNarrativePermission } from '../../hooks/useNarrativePermission';
import { useUpdateNarrativeAnnouncement } from '../../hooks/useUpdateNarrativeAnnouncement';
import { useDeleteNarrativeAnnouncement } from '../../hooks/useDeleteNarrativeAnnouncement';
import { Narrative } from '../../hooks/useNarrative';
import { AnnouncementModal } from '../../components/AnnouncementModal';

export type Props = {
  slug: string;
  announcement: NonNullable<Narrative['announcements']>[number];
};

export const AnnouncementActions = ({ slug, announcement }: Props) => {
  const { mutate } = useDeleteNarrativeAnnouncement(slug);
  const [modalState, setModalState] = useState<'idle' | 'editing' | 'deleting'>(
    'idle'
  );

  const { mutate: updateAnnouncement } = useUpdateNarrativeAnnouncement({
    slug,
    onSuccess: () => setModalState('idle'),
  });
  const { narrativePermissions } = useNarrativePermission();

  const canModifyAnnouncement =
    narrativePermissions?.canUpdateAnnouncement &&
    narrativePermissions?.canDeleteAnnouncement;

  const options = useRef([
    { label: 'Edit', value: 'EDIT', icon: <EditOutline /> },
    { label: 'Delete', value: 'DELETE', icon: <BinSolid /> },
  ]).current;

  const handleSelect = (value: string) => {
    switch (value) {
      case 'DELETE':
        setModalState('deleting');
        break;
      case 'EDIT':
        setModalState('editing');
        break;
    }
  };

  const handleClose = () => {
    setModalState('idle');
  };

  if (!canModifyAnnouncement || typeof announcement === undefined) {
    return null;
  }

  return (
    <>
      <Box height="24px">
        <ButtonWithDropdown
          name="more"
          styleType="passive"
          data-cy-id="announcement-more"
          options={options}
          onSelect={handleSelect}
        >
          <MoreHorizontalSolid />
        </ButtonWithDropdown>
      </Box>
      <If match={modalState === 'deleting'}>
        {() => (
          <Modal
            title="Delete Announcement"
            onClose={handleClose}
            primaryLabel="Delete"
            onPrimaryClick={() => {
              mutate(announcement.id);
              handleClose();
            }}
            secondaryLabel="Cancel"
            onSecondaryClick={handleClose}
            fullscreen
          >
            <Text typography="small" color="black">
              We&apos;re about to remove this announcement permanently. Are you
              sure?
            </Text>
          </Modal>
        )}
      </If>
      <If match={modalState === 'editing'}>
        {() => (
          <AnnouncementModal
            isOpen={modalState === 'editing'}
            announcement={announcement}
            onSubmit={(contentHtml) =>
              updateAnnouncement({ contentHtml, id: announcement.id })
            }
            ctaText={'Update'}
            onClose={handleClose}
          />
        )}
      </If>
    </>
  );
};
